import { useState } from 'react';

import TABLE_CONSTANTS from '@constants/admin/tables';
import { message, Row } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';

import ResizableTable from '@/components/admin/ResizableTable';
import SectionHeading from '@/components/admin/SectionHeading';
import TextInput from '@/components/admin/TextInput';
import { ErrorTypeEnum } from '@/enums/error-type.enum';
import {
    DistributorDto,
    SortEnumType,
    useGetDistributorsQuery,
    useUpdateDistributorMutation
} from '@/graphql/main';
import usePaginate from '@/hooks/admin/usePaginate';
import useError from '@/hooks/error/useError';
import useColDefs from '@/pages/admin/CommercialAgreements/Distributors/hooks/useColDefs';
import CreateDistributorsModal from '@/pages/admin/CommercialAgreements/Distributors/partials/CreateDistributorsModal';

const DistributorsTable = () => {
    const {
        handlePageChange,
        pageSize,
        itemsToSkip,
        setTotalItems,
        totalItems,
        sortingOrder,
        setSortingOrder
    } = usePaginate();
    const { setError, clearError } = useError();
    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');

    const { loading, refetch, data } = useGetDistributorsQuery({
        variables: {
            searchText,
            take: pageSize,
            skip: itemsToSkip,
            order: sortingOrder
        },
        onCompleted: (data) => {
            if (data?.distributors?.items) {
                setTotalItems(data.distributors.totalCount);
            }
            clearError();
        },
        onError: (error) => {
            setError(error, ErrorTypeEnum.ApolloError);
        }
    });
    const [updateDistributorMutation] = useUpdateDistributorMutation({
        onError: (error) => setError(error, ErrorTypeEnum.ApolloError),
        onCompleted: async () => {
            message.success(t('DISTRIBUTOR_UPDATED_SUCCESSFULLY'));
            await refetch();
        }
    });

    const handeOnRowChange = async (row: DistributorDto) =>
        await updateDistributorMutation({
            variables: { input: { distributorId: row.id, name: row.name } }
        });

    const { colDef } = useColDefs({
        onChangeSuccess: async () => await refetch()
    });
    const onSortChange = (sorterResult: SorterResult<DistributorDto>) =>
        setSortingOrder(
            sorterResult.order
                ? {
                      [sorterResult.columnKey as string]:
                          sorterResult.order === 'ascend' ? SortEnumType.Asc : SortEnumType.Desc
                  }
                : undefined
        );

    return (
        <>
            <SectionHeading heading={t('DISTRIBUTORS')} />
            <Row justify={'space-between'} align={'bottom'} style={{ marginBottom: 18 }}>
                <TextInput inputValue={searchText} setInputValue={setSearchText} />
                <CreateDistributorsModal successCallback={refetch} />
            </Row>
            <ResizableTable
                onRowChange={handeOnRowChange}
                colDefs={colDef}
                tableData={(data?.distributors?.items as DistributorDto[]) || []}
                emptyText={t('NO_RESULTS')}
                loading={loading}
                onPageChange={handlePageChange}
                totalPaginationItems={totalItems}
                size={TABLE_CONSTANTS.TableSize}
                onChange={(_, __, sorterResult) =>
                    onSortChange(sorterResult as SorterResult<DistributorDto>)
                }
            />
        </>
    );
};

export default DistributorsTable;
